/**
 * Owner self-service API client (sub-project ⑤B). Talks to the ⑤A owner engine
 * (`/api/owner`, behind `checkAuth`):
 *   • GET    /projects                       → the owner dashboard (`OwnerDashboardProject[]`).
 *   • POST   /claims                         → initiate a claim (email-domain auto-verify or pending).
 *   • POST   /claims/verify                  → complete a ④A token claim (`?token=`).
 *   • POST   /raises/:slug/release            → release the caller's verified claim.
 *   • PUT    /raises/:slug/fields/:field     → authorized owner edit (→ ② overlay).
 *   • DELETE /raises/:slug/fields/:field     → authorized owner revert.
 *   • POST   /raises/:slug/offering-classification/attest → owner public-state attestation.
 *   • DELETE /raises/:slug/offering-classification/attest → withdraw + re-restrict.
 *   • GET    /connect-requests               → the owner's pending requests (non-PII facets).
 *   • POST   /connect-requests/:id/accept    → authed-owner accept (non-PII outcome).
 *   • POST   /connect-requests/:id/decline   → authed-owner decline (non-PII outcome).
 *   • GET    /notifications                  → owner-scoped in-app events + unread count.
 *   • POST   /notifications/:id/read         → mark only the caller's notification read.
 *
 * Mirrors `connectApi.ts`:
 *   • the outer `{ success, data }` envelope is unwrapped to the inner `data`,
 *   • a readable error message is extracted from non-2xx bodies (`{ error }` JSON
 *     or text),
 *   • we reuse `createAuthHeaders` (Bearer token attached only when signed in)
 *     and `fetchWithActivity` (activity tracking + session refresh).
 *
 * TYPED ERROR MAPPING — the owner flows are small state machines the claim page,
 * edit mode, and dashboard must branch on cleanly, so the backend's documented
 * status codes are translated into discriminable error classes:
 *   • 403 → {@link OwnerForbiddenError}      (not the verified owner; surfaced as an error)
 *   • 404 → {@link OwnerNotFoundError}       (slug/project/request not found)
 *   • 409 → {@link ClaimConflictError}       (already a verified owner / already-decided request)
 *   • 400 → context-dependent: {@link ClaimInvalidTokenError} (token verify),
 *           {@link OwnerEditValidationError} (field edit), else a generic {@link OwnerApiError}
 *   • any other non-2xx → {@link OwnerApiError} (carries the status + message)
 *
 * SUCCESS is "any 2xx", not strictly 200: `initiateClaim`/`verifyClaim` return 201.
 *
 * SECURITY (Hard rule, inherited from ⑤A): every response unwrapped here is the
 * owner's OWN claim, their own dashboard (counts only), a resolved field, or the
 * non-PII connect decision outcome (`id`/`status`/`ownerActionAt`) — never any
 * third-party contact PII. The owner uid is NEVER sent in a body; the server
 * derives it from the authenticated request.
 */

import {
  assertAuthIdentity,
  captureAuthIdentity,
  createAuthHeaders,
  fetchWithActivity,
  getApiBaseUrl,
} from '@/utils/apiUtils';
import type {
  ConnectDecisionOutcome,
  OwnerBillingSession,
  OwnerBillingStateResult,
  OwnerBillingStatus,
  OwnerConnectAction,
  OwnerDashboardProject,
  OwnerEditValue,
  OwnerNotification,
  OwnerPendingRequest,
  OwnerViewsAnalytics,
  ProjectClaim,
  ProjectPromotionState,
  PromotionCheckoutSession,
  ResolvedOwnerField,
} from '@/types/owner';
import type {
  CapitalStackRunDto,
  CapitalStackUnsupportedReason,
  CapitalStackVisibilityDto,
  CandidateDossierDto,
  CandidateDossierPage,
  CandidateDossierDeal,
  CandidateDossierClaim,
} from '@/components/raises/capitalStack/types';
import type { CoverageBlock } from '@/components/raises/capitalStack/coverageTypes';
import type { OfferingClassification } from '@/types/raises';

/**
 * ADMIN read-only snapshot of one proponent's (verified claimant's) owner console,
 * returned by `GET /api/owner/admin/proponents/:ownerUid/dashboard` (behind
 * `checkAdmin`). It is the SAME projection the target owner sees on `/owner`, scoped
 * server-side to the target `ownerUid` — the admin never acts on the owner's behalf
 * through it (READ-ONLY endpoint), and every call is audited server-side (§4.3).
 *
 * A uid that owns no verified claims is not a proponent → the endpoint returns 404
 * `owner_proponent_not_found` (mapped to {@link OwnerNotFoundError} by {@link request}),
 * NOT an empty dashboard.
 *
 * SAFETY: identical to the owner dashboard — counts + the investor's OWN consented
 * facets only, never the target owner's contact PII beyond what their own `/owner`
 * dashboard already exposes.
 */
export interface AdminProponentDashboard {
  /** The target proponent's uid (echoed by the server; never derived from the admin). */
  ownerUid: string;
  /** The proponent's claimed projects — same shape/order as their own `/owner` dashboard. */
  projects: OwnerDashboardProject[];
  /** The proponent's PENDING Connect requests — same shape as their `GET /connect-requests`. */
  pendingConnectRequests: OwnerPendingRequest[];
  /** The proponent's billing state summary, or `null` when there is none. */
  billing: OwnerBillingStateResult | null;
}

/** The outer transport envelope every ⑤A owner route returns on success. */
interface ApiEnvelope<T> {
  success: true;
  data: T;
}

/**
 * Distinguishes the 400-error semantics per call site. The owner router returns
 * 400 for both an invalid/used claim token (`POST /claims/verify`) and an invalid
 * field/value owner edit (`PUT /raises/:slug/fields/:field`); the client maps 400
 * to the right typed error based on which call issued the request.
 */
type BadRequestKind = 'token' | 'edit' | 'generic';

/** Preset reasons an owner may give when releasing their verified claim. */
export type ReleaseClaimReason =
  | 'not_my_project'
  | 'claimed_by_mistake'
  | 'no_longer_managing'
  | 'other';

/** Ordered options shared by every owner release surface. */
export const RELEASE_CLAIM_REASONS = [
  { value: 'not_my_project', label: "This isn't my project" },
  { value: 'claimed_by_mistake', label: 'Claimed by mistake' },
  { value: 'no_longer_managing', label: 'No longer managing the project' },
  { value: 'other', label: 'Other' },
] as const satisfies ReadonlyArray<{ value: ReleaseClaimReason; label: string }>;

// ── Typed errors ──────────────────────────────────────────────────────────────

/**
 * Base error for every Owner API failure. Carries the originating HTTP `status`
 * so callers can branch generically; the specific subclasses let the owner UI
 * `instanceof`-switch to the right state without parsing strings. Messages are
 * domain-safe (no contact PII — they come from ⑤A's `{ error }` bodies, which
 * never carry PII).
 */
export class OwnerApiError extends Error {
  readonly status: number;
  constructor(message: string, status = 0) {
    super(message);
    this.name = 'OwnerApiError';
    this.status = status;
    // Preserve the prototype chain across the TS/ES5 `extends Error` transpile so
    // `instanceof` keeps working for the subclasses below.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * HTTP 403 — authenticated but NOT the verified owner of the target project (no
 * `verified` claim) and not an admin override. The owner UI surfaces this as an
 * error: edit affordances are a frontend-only convenience, but ⑤A's `isOwner` is
 * the real authz, so a non-owner who forces a save (or accept/decline) gets 403.
 * NO mutation occurred (the server fails closed before any side effect).
 */
export class OwnerForbiddenError extends OwnerApiError {
  constructor(message = 'You are not the verified owner of this project.') {
    super(message, 403);
    this.name = 'OwnerForbiddenError';
  }
}

/**
 * HTTP 404 — the slug, project, or connect request did not resolve to a live row.
 * (For a claim/edit this means the raise slug is unknown; for a connect action it
 * means no such request.)
 */
export class OwnerNotFoundError extends OwnerApiError {
  constructor(message = 'The requested project or request was not found.') {
    super(message, 404);
    this.name = 'OwnerNotFoundError';
  }
}

/**
 * HTTP 409 — a conflicting state: the caller is already a verified owner (claim
 * initiate), or the connect request was already decided the other way. The UI
 * routes this to a "you already own this / this request was already handled"
 * state rather than a hard error.
 */
export class ClaimConflictError extends OwnerApiError {
  constructor(message = 'This action conflicts with the current state.') {
    super(message, 409);
    this.name = 'ClaimConflictError';
  }
}

/**
 * HTTP 400 from `POST /claims/verify` — the ④A claim token is invalid, expired, or
 * already used. The claim page routes this to its "this claim link is no longer
 * valid" state.
 */
export class ClaimInvalidTokenError extends OwnerApiError {
  constructor(message = 'This claim link is invalid or has already been used.') {
    super(message, 400);
    this.name = 'ClaimInvalidTokenError';
  }
}

/**
 * HTTP 400 from `PUT /raises/:slug/fields/:field` — ② rejected the edit (the field
 * is not owner-editable, or the value is invalid for the field type). The edit
 * field surfaces this inline so the owner can correct the input.
 */
export class OwnerEditValidationError extends OwnerApiError {
  constructor(message = 'That value is not valid for this field.') {
    super(message, 400);
    this.name = 'OwnerEditValidationError';
  }
}

/**
 * HTTP 422 `capital_stack_unsupported` from the capital-stack GENERATE routes — the
 * listing is missing (or carries an invalid) fact the matcher requires, so no stack
 * could be modelled. The stable `reason` discriminant (safe to echo — it names the
 * missing/invalid project facet, never any candidate data) lets the panel render a
 * specific, actionable message ("This listing is missing a country/sector…"). NO run
 * was created (the server fails closed before the matcher). `reason` is `null` only
 * if the body somehow omitted it (defensive — the backend always sends it).
 */
export class CapitalStackUnsupportedError extends OwnerApiError {
  readonly reason: CapitalStackUnsupportedReason | null;
  constructor(message: string, reason: CapitalStackUnsupportedReason | null = null) {
    super(message, 422);
    this.name = 'CapitalStackUnsupportedError';
    this.reason = reason;
  }
}

/**
 * HTTP 429 from a capital-stack GENERATE route — the per-uid generation rate limit
 * (5/hr) was exceeded (the matcher is a heavy LLM-assisted query). The UI surfaces
 * this as a friendly, transient toast; NO run was created. Distinct from the other
 * typed errors so the generate mutation can special-case it.
 */
export class CapitalStackRateLimitError extends OwnerApiError {
  constructor(message = 'You have generated too many capital stacks recently. Please try again later.') {
    super(message, 429);
    this.name = 'CapitalStackRateLimitError';
  }
}

// ── M2 owner-billing typed error ──────────────────────────────────────────────

/**
 * The stable, client-actionable owner-billing error codes (from
 * `my-backend/.../billing/errors.ts`). The FE branches on `code` (and `status`),
 * never on the human-readable message.
 */
export type OwnerBillingErrorCode =
  | 'owner_subscription_required' // 402 (gated action; not the billing page)
  | 'owner_billing_unavailable' // 503 transient (persistence unreadable)
  | 'owner_billing_provider_unavailable' // 503 transient (Stripe upstream)
  | 'owner_billing_not_configured' // 501 deliberate fail-closed (Stripe off)
  | 'owner_verified_claim_required' // 403 (should not reach the billing page)
  | 'owner_billing_checkout_conflict' // 409 → route to portal
  | 'owner_billing_trial_alignment_unavailable'; // 409 → final-48h state

/**
 * A typed owner-billing failure. Carries the originating HTTP `status` (so the
 * hooks can split 501 = deliberate-config from 503 = transient), the stable server
 * `code`, and — for a `402 owner_subscription_required` — the billing `billingStatus`
 * + `trialEndsAt` the server echoed so the paywall interstitial can render an
 * accurate, honest line ("Your trial ended on {date}.") rather than guessing.
 *
 * Distinct from {@link OwnerApiError} so a billing consumer can `instanceof`-switch
 * without colliding with the claim/edit/capital-stack error classes.
 */
export class OwnerBillingApiError extends Error {
  readonly status: number;
  readonly code: OwnerBillingErrorCode | null;
  /** The 402 body's billing status (e.g. 'canceled'); null for non-402 errors. */
  readonly billingStatus: OwnerBillingStatus | null;
  /** The 402 body's trial-end ISO date; null when absent / non-402. */
  readonly trialEndsAt: string | null;

  constructor(
    message: string,
    status: number,
    options: {
      code?: OwnerBillingErrorCode | null;
      billingStatus?: OwnerBillingStatus | null;
      trialEndsAt?: string | null;
    } = {},
  ) {
    super(message);
    this.name = 'OwnerBillingApiError';
    this.status = status;
    this.code = options.code ?? null;
    this.billingStatus = options.billingStatus ?? null;
    this.trialEndsAt = options.trialEndsAt ?? null;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// ── Error parsing ─────────────────────────────────────────────────────────────

/** Best-effort human-readable message from an arbitrary error body. */
function errorText(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.message === 'string') return record.message;
    if (typeof record.error === 'string') return record.error;
    if (typeof record.error === 'object') return errorText(record.error);
    try {
      return JSON.stringify(record);
    } catch {
      return null;
    }
  }
  return String(value);
}

/** Reads a readable error message from a non-2xx response (JSON `{ error }` or text). */
async function readError(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const body = await response.json().catch(() => null);
    return (
      errorText((body as { error?: unknown } | null)?.error ?? body) ||
      `Owner request failed (${response.status})`
    );
  }
  const text = await response.text().catch(() => '');
  return text || `Owner request failed (${response.status})`;
}

/**
 * Maps a non-2xx response to the right typed error so the owner UI can branch.
 * The 400 mapping is context-dependent (`badRequestKind`): a token-verify 400 is a
 * {@link ClaimInvalidTokenError}, a field-edit 400 is an {@link OwnerEditValidationError},
 * any other 400 is a generic {@link OwnerApiError}. The server's message
 * (domain-safe, no PII) is preserved on every error.
 */
async function toTypedError(response: Response, badRequestKind: BadRequestKind): Promise<OwnerApiError> {
  const message = await readError(response);
  switch (response.status) {
    case 403:
      return new OwnerForbiddenError(message);
    case 404:
      return new OwnerNotFoundError(message);
    case 409:
      return new ClaimConflictError(message);
    case 400:
      if (badRequestKind === 'token') return new ClaimInvalidTokenError(message);
      if (badRequestKind === 'edit') return new OwnerEditValidationError(message);
      return new OwnerApiError(message, 400);
    default:
      return new OwnerApiError(message, response.status);
  }
}

/** URL-safe encoding for a path segment (slugs are kebab-case, but be defensive). */
function encodeSegment(segment: string): string {
  return encodeURIComponent(segment);
}

/**
 * Builds the `?after=&limit=` query for the candidate deals/claims continuation reads.
 * Only finite, present values are appended; an empty params object yields an empty
 * string (the server applies its own defaults).
 */
function candidateListQuery(params: { after?: number; limit?: number }): string {
  const search = new URLSearchParams();
  if (typeof params.after === 'number' && Number.isFinite(params.after)) {
    search.set('after', String(params.after));
  }
  if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
    search.set('limit', String(params.limit));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/**
 * Issues a request against a ⑤A owner path and unwraps the outer `{ success, data }`
 * envelope. ANY 2xx is success (notably 201 from the claim routes). A non-2xx is
 * translated to the right typed error (the 400 kind disambiguated per call site).
 */
async function request<T>(
  path: string,
  init: { method: 'GET' | 'PUT' | 'POST' | 'DELETE'; body?: unknown; badRequestKind?: BadRequestKind },
): Promise<T> {
  // §4.3 identity safety (CX#7): capture ONE identity snapshot at entry and thread it through
  // header construction, delivery, and every post-body-read fence, so an account switch A→B in
  // an await gap can never send A's bearer/CSRF under B, nor deliver A's body into B's state.
  const requestIdentity = captureAuthIdentity();
  const headers = await createAuthHeaders({}, init.method, requestIdentity);
  const response = await fetchWithActivity(`${getApiBaseUrl()}${path}`, {
    method: init.method,
    headers,
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  }, requestIdentity);

  if (!response.ok) {
    const error = await toTypedError(response, init.badRequestKind ?? 'generic');
    assertAuthIdentity(requestIdentity);
    throw error;
  }

  const json = (await response.json()) as ApiEnvelope<T>;
  assertAuthIdentity(requestIdentity);
  return json.data;
}

/**
 * The set of `reason` discriminants the capital-stack 422 may carry. A body whose
 * `reason` is outside this set (or absent) maps to a `null` reason on the typed
 * error, so the panel falls back to a generic-but-honest message rather than
 * trusting an unexpected string.
 */
const CAPITAL_STACK_REASONS: readonly CapitalStackUnsupportedReason[] = [
  'no_country',
  'unmappable_sector',
  'no_capital_need',
  'invalid_match_input',
];

function toCapitalStackReason(value: unknown): CapitalStackUnsupportedReason | null {
  return typeof value === 'string' &&
    (CAPITAL_STACK_REASONS as readonly string[]).includes(value)
    ? (value as CapitalStackUnsupportedReason)
    : null;
}

/**
 * Capital-stack request path — a thin sibling of {@link request} that additionally
 * maps the two capital-stack-specific statuses the generic mapper does not model:
 *   • 422 `capital_stack_unsupported` → {@link CapitalStackUnsupportedError} (reads the
 *     JSON body ONCE for the stable `reason` discriminant + safe `error` message);
 *   • 429 (generation rate limit) → {@link CapitalStackRateLimitError}.
 * Every other non-2xx delegates to {@link toTypedError} (403/404/etc.). The success
 * envelope unwrap is identical (the GET route's `data` may legitimately be `null`).
 */
async function capitalStackRequest<T>(
  path: string,
  init: { method: 'GET' | 'POST' | 'PATCH'; body?: unknown },
): Promise<T> {
  // §4.3 identity safety (CX#7): capture ONE identity snapshot at entry and thread it through
  // header construction, delivery, and every post-body-read fence (including EVERY error-body
  // read below), so an account switch A→B in an await gap can never send A's bearer/CSRF under
  // B, nor deliver A's body/error into B's state.
  const requestIdentity = captureAuthIdentity();
  const headers = await createAuthHeaders({}, init.method, requestIdentity);
  const response = await fetchWithActivity(`${getApiBaseUrl()}${path}`, {
    method: init.method,
    headers,
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  }, requestIdentity);

  if (!response.ok) {
    if (response.status === 429) {
      const message = await readError(response);
      assertAuthIdentity(requestIdentity);
      throw new CapitalStackRateLimitError(message);
    }
    if (response.status === 422) {
      // Read the JSON body once for the `{ error, code, reason }` shape; fall back to
      // the plain text/status message if it is not the expected JSON.
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        const body = (await response.json().catch(() => null)) as
          | { error?: unknown; reason?: unknown }
          | null;
        assertAuthIdentity(requestIdentity);
        const message =
          errorText(body?.error) || 'Capital stack could not be generated (422)';
        throw new CapitalStackUnsupportedError(message, toCapitalStackReason(body?.reason));
      }
      const text = await response.text().catch(() => '');
      assertAuthIdentity(requestIdentity);
      throw new CapitalStackUnsupportedError(text || 'Capital stack could not be generated (422)');
    }
    if (response.status === 402) {
      // A gated generate/publish where the owner's subscription lapsed. The BE returns
      // the FULL billing 402 body (`{ error, code, status, trialEndsAt }`); map it to a
      // typed OwnerBillingApiError (via the shared billing parser) so the container can
      // render the inline paywall from the ACTUAL payload — the trial-end date, the
      // stable code — rather than dropping them into a generic error/toast (#11).
      const error = await toBillingError(response);
      assertAuthIdentity(requestIdentity);
      throw error;
    }
    const error = await toTypedError(response, 'generic');
    assertAuthIdentity(requestIdentity);
    throw error;
  }

  const json = (await response.json()) as ApiEnvelope<T>;
  assertAuthIdentity(requestIdentity);
  return json.data;
}

// ── M2 owner-billing request path ─────────────────────────────────────────────

interface OwnerBillingErrorResponseBody {
  error?: unknown;
  code?: unknown;
  status?: unknown;
  trialEndsAt?: unknown;
}

/** The set of stable billing codes; a body carrying anything else maps to `null`. */
const OWNER_BILLING_CODES: readonly OwnerBillingErrorCode[] = [
  'owner_subscription_required',
  'owner_billing_unavailable',
  'owner_billing_provider_unavailable',
  'owner_billing_not_configured',
  'owner_verified_claim_required',
  'owner_billing_checkout_conflict',
  'owner_billing_trial_alignment_unavailable',
];

function toBillingCode(value: unknown): OwnerBillingErrorCode | null {
  return typeof value === 'string' &&
    (OWNER_BILLING_CODES as readonly string[]).includes(value)
    ? (value as OwnerBillingErrorCode)
    : null;
}

/** The billing statuses a 402 body may echo (`status` field). */
const OWNER_BILLING_STATUSES: readonly OwnerBillingStatus[] = [
  'trialing',
  'active',
  'past_due',
  'canceled',
  'none',
];

function toBillingStatus(value: unknown): OwnerBillingStatus | null {
  return typeof value === 'string' &&
    (OWNER_BILLING_STATUSES as readonly string[]).includes(value)
    ? (value as OwnerBillingStatus)
    : null;
}

/**
 * Reads a non-2xx billing response body ONCE for the `{ error, code, status,
 * trialEndsAt }` shape the billing router returns (see M1 `ownerBillingErrorBody`)
 * and constructs the typed {@link OwnerBillingApiError}. The `status`/`trialEndsAt`
 * fields are present only on the `402 owner_subscription_required` body; they are
 * preserved so a paywall interstitial can render an accurate, honest line.
 */
async function toBillingError(response: Response): Promise<OwnerBillingApiError> {
  const contentType = response.headers.get('content-type') ?? '';
  let body: OwnerBillingErrorResponseBody | null = null;
  if (contentType.includes('application/json')) {
    body = (await response.json().catch(() => null)) as OwnerBillingErrorResponseBody | null;
  }
  const message =
    errorText(body?.error) || `Owner billing request failed (${response.status})`;
  return new OwnerBillingApiError(message, response.status, {
    code: toBillingCode(body?.code),
    billingStatus: toBillingStatus(body?.status),
    trialEndsAt: typeof body?.trialEndsAt === 'string' ? body.trialEndsAt : null,
  });
}

/**
 * Issues a request against a billing path and unwraps the `{ success, data }`
 * envelope. ANY 2xx is success (checkout/portal return 201). A non-2xx becomes a
 * typed {@link OwnerBillingApiError} carrying the server `status` + `code` (and, for
 * a 402, the billing status + trial date). The billing status/code split is what
 * lets `useOwnerBillingState` NOT retry a 501 (deliberate config) while retrying a
 * 503 (transient).
 */
async function billingRequest<T>(
  path: string,
  init: { method: 'GET' | 'POST'; body?: unknown },
): Promise<T> {
  // §4.3 identity safety (CX#7): capture ONE identity snapshot at entry and thread it through
  // header construction, delivery, and every post-body-read fence, so an account switch A→B in
  // an await gap can never send A's bearer/CSRF under B, nor deliver A's body into B's state.
  const requestIdentity = captureAuthIdentity();
  const headers = await createAuthHeaders({}, init.method, requestIdentity);
  const response = await fetchWithActivity(`${getApiBaseUrl()}${path}`, {
    method: init.method,
    headers,
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  }, requestIdentity);

  if (!response.ok) {
    const error = await toBillingError(response);
    assertAuthIdentity(requestIdentity);
    throw error;
  }

  const json = (await response.json()) as ApiEnvelope<T>;
  assertAuthIdentity(requestIdentity);
  return json.data;
}

/**
 * Durable version tag recorded with the verbatim VIS1 draft attestation copy.
 * Keep this beside the transport contract so owner and admin attestations can
 * never silently submit different wording versions.
 */
export const OFFERING_ATTESTATION_COPY_VERSION = 'vis1-attestation-draft-v1' as const;

/** The only two classifications that may leave the fail-closed floor. */
export type PublicOfferingClassification = Exclude<
  OfferingClassification,
  'security_restricted'
>;

/** Body accepted by both the owner and admin attestation twins. */
export interface OfferingClassificationAttestationInput {
  classification: PublicOfferingClassification;
  acceptCopyVersion: string;
  counselReference?: string;
}

/** Minimal mutation response; the refreshed raise DTO remains the read model. */
export interface OfferingClassificationMutationResult {
  effectiveClassification: OfferingClassification;
}

/** Admin-only latest attestation detail. Counsel reference never enters public/owner DTOs. */
export interface OfferingClassificationAttestationDetail {
  actorUid: string;
  actorRole: 'owner' | 'admin';
  classification: PublicOfferingClassification;
  action: 'attested' | 'withdrawn';
  attestationCopyVersion: string | null;
  counselReference: string | null;
  createdAt: string;
}

/** Admin-only moderation read for one listing. Every value is server-authored. */
export interface OfferingClassificationDetail {
  effectiveClassification: OfferingClassification;
  derivedFloor: 'security_restricted';
  overlayValue: OfferingClassification | null;
  latestAttestation: OfferingClassificationAttestationDetail | null;
}

/** Admin-only compliance-worklist row. Deliberately excludes counsel free text. */
export interface PublicExposedClassificationItem {
  id: number;
  slug: string | null;
  name: string;
  effectiveClassification: PublicOfferingClassification;
  latestAttestationActor: {
    actorUid: string;
    actorRole: 'owner' | 'admin';
  } | null;
}

/** Client pagination model over the backend's bounded worklist array. */
export interface PublicExposedClassificationPage {
  listings: PublicExposedClassificationItem[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

export function adminEditRaiseField(
  slug: string,
  field: string,
  value: OwnerEditValue,
): Promise<ResolvedOwnerField> {
  return request<ResolvedOwnerField>(
    `/api/owner/admin/raises/${encodeSegment(slug)}/fields/${encodeSegment(field)}`,
    { method: 'PUT', body: { value }, badRequestKind: 'edit' },
  );
}

export const ownerApi = {
  /**
   * GET /api/owner/projects — the authed owner's dashboard: every claimed project
   * with its claim status, profile-completeness, pending Connect-request count, and
   * interest analytics (counts only — no investor identity / contact PII). The
   * owner uid is derived from auth, never sent.
   */
  getOwnerDashboard: (): Promise<OwnerDashboardProject[]> =>
    request<OwnerDashboardProject[]>('/api/owner/projects', { method: 'GET' }),

  /**
   * GET /api/owner/connect-requests — the authed owner's PENDING ('requested')
   * Connect requests across all their verified-claimed projects, newest first. Each
   * carries the request id (for accept/decline), the targeted project (id / name /
   * slug, for grouping), and the investor's OWN consented facets (firm / investor
   * type / ticket) + note — NEVER any investor identity or contact PII. Unwraps the
   * `{ success, data: { requests } }` envelope to the `requests` array. The owner uid
   * is derived from auth, never sent.
   */
  getOwnerPendingRequests: (): Promise<OwnerPendingRequest[]> =>
    request<{ requests: OwnerPendingRequest[] }>('/api/owner/connect-requests', {
      method: 'GET',
    }).then((data) => data.requests),

  /**
   * GET /api/owner/notifications — one owner-scoped snapshot containing the most
   * recent in-app events plus the total unread count. The server derives owner uid
   * from auth. Paging values are serialized as query parameters only; no identity
   * or contact data is sent by the client.
   */
  getOwnerNotifications: (
    limit = 10,
    offset = 0,
  ): Promise<{ notifications: OwnerNotification[]; unreadCount: number }> => {
    if (!Number.isSafeInteger(limit) || limit < 1) {
      throw new RangeError('Notification limit must be a positive integer.');
    }
    if (!Number.isSafeInteger(offset) || offset < 0) {
      throw new RangeError('Notification offset must be a non-negative integer.');
    }
    const search = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    return request<{ notifications: OwnerNotification[]; unreadCount: number }>(
      `/api/owner/notifications?${search.toString()}`,
      { method: 'GET' },
    );
  },

  /**
   * POST /api/owner/notifications/:id/read — idempotently marks the caller's own
   * notification read. The owner uid is never accepted from the browser; backend
   * authorization scopes the update by both notification id and authenticated uid.
   */
  markNotificationRead: (notificationId: number): Promise<void> =>
    request<void>(
      `/api/owner/notifications/${encodeSegment(String(notificationId))}/read`,
      { method: 'POST' },
    ),

  /**
   * POST /api/owner/claims — initiate a claim for a project. The owner asserts their
   * OWN `workEmail` (its domain is matched against the sponsor website for
   * auto-verification → `verified`; otherwise → `pending` admin review). Returns the
   * resulting {@link ProjectClaim} (the owner's own). 409 → already a verified owner;
   * 404 → no such project.
   */
  initiateClaim: (projectId: number, workEmail: string): Promise<ProjectClaim> =>
    request<ProjectClaim>('/api/owner/claims', {
      method: 'POST',
      body: { projectId, workEmail },
    }),

  /**
   * POST /api/owner/raises/:slug/release — release the caller's own VERIFIED
   * claim. The server derives the owner uid from auth and accepts only a preset
   * reason. 404 → {@link OwnerNotFoundError} when the caller has no verified claim
   * on the listing; 400 → generic {@link OwnerApiError} for an invalid reason.
   */
  releaseClaim: (slug: string, reason: ReleaseClaimReason): Promise<ProjectClaim> =>
    request<ProjectClaim>(`/api/owner/raises/${encodeSegment(slug)}/release`, {
      method: 'POST',
      body: { reason },
    }),

  /**
   * POST /api/owner/claims/verify — complete a demand-triggered token claim. `token`
   * is the ④A single-use signed 'claim' token (from the `?token=` query param); a
   * valid token verifies the claim immediately. An optional `workEmail` is included
   * only when non-blank. 400 → {@link ClaimInvalidTokenError} (invalid/used token).
   */
  verifyClaim: (token: string, workEmail?: string | null): Promise<ProjectClaim> => {
    const trimmed = typeof workEmail === 'string' ? workEmail.trim() : '';
    const body = trimmed.length > 0 ? { token, workEmail: trimmed } : { token };
    return request<ProjectClaim>('/api/owner/claims/verify', {
      method: 'POST',
      body,
      badRequestKind: 'token',
    });
  },

  /**
   * POST /api/owner/claims/verify-email — complete the work-email verification
   * round-trip. `token` is the single-use signed link emailed to the asserted work
   * address; the server flips the PENDING `email_verify` claim to `verified` ONLY
   * when the caller is signed in as the uid that initiated it (confused-deputy
   * guard → 403 {@link OwnerForbiddenError}). 400 → {@link ClaimInvalidTokenError}
   * (invalid/expired/used link); 404 → {@link OwnerNotFoundError}; 409 →
   * {@link ClaimConflictError}.
   */
  verifyClaimEmail: (token: string): Promise<ProjectClaim> =>
    request<ProjectClaim>('/api/owner/claims/verify-email', {
      method: 'POST',
      body: { token },
      badRequestKind: 'token',
    }),

  /**
   * PUT /api/owner/raises/:slug/fields/:field — apply an authorized owner edit to a
   * field (→ ② overlay after the server's `isOwner` gate). Returns the resolved
   * field (value + provenance + `ownerUpdated`) so the UI can render the updated
   * value with the correct `ProvenanceTag`. A `null` value deliberately clears the
   * field. 403 → {@link OwnerForbiddenError}; 400 → {@link OwnerEditValidationError}.
   */
  editOwnerField: (
    slug: string,
    field: string,
    value: OwnerEditValue,
  ): Promise<ResolvedOwnerField> =>
    request<ResolvedOwnerField>(
      `/api/owner/raises/${encodeSegment(slug)}/fields/${encodeSegment(field)}`,
      { method: 'PUT', body: { value }, badRequestKind: 'edit' },
    ),

  /**
   * DELETE /api/owner/raises/:slug/fields/:field — revert (remove) the owner
   * override for a field, restoring the sourced/normalized value. Returns the
   * resolved field as the read-model now resolves it. 403 → {@link OwnerForbiddenError}.
   */
  revertOwnerField: (slug: string, field: string): Promise<ResolvedOwnerField> =>
    request<ResolvedOwnerField>(
      `/api/owner/raises/${encodeSegment(slug)}/fields/${encodeSegment(field)}`,
      { method: 'DELETE', badRequestKind: 'edit' },
    ),

  /**
   * Owner-only public-state transition. The server authorizes ownership and
   * atomically records the attestation plus overlay; the client never writes a
   * public classification through the generic field-edit route.
   */
  attestOfferingClassification: (
    slug: string,
    input: OfferingClassificationAttestationInput,
  ): Promise<OfferingClassificationMutationResult> =>
    request<OfferingClassificationMutationResult>(
      `/api/owner/raises/${encodeSegment(slug)}/offering-classification/attest`,
      { method: 'POST', body: input, badRequestKind: 'edit' },
    ),

  /** Owner-only one-click safety action: withdraw the attestation and re-restrict. */
  withdrawOfferingClassificationAttestation: (
    slug: string,
  ): Promise<OfferingClassificationMutationResult> =>
    request<OfferingClassificationMutationResult>(
      `/api/owner/raises/${encodeSegment(slug)}/offering-classification/attest`,
      { method: 'DELETE', badRequestKind: 'edit' },
    ),

  /**
   * GET /api/owner/admin/proponents/:ownerUid/dashboard — ADMIN read-only view of a
   * verified proponent's OWNER console (their projects + pending Connect requests +
   * billing summary). Authorized by `checkAdmin`; the server scopes the projection to
   * the TARGET `ownerUid` (never the admin's own claims) and audits the access. Unwraps
   * the `{ success, data }` envelope via the shared {@link request}. A uid that owns no
   * verified claims → `404 owner_proponent_not_found`, surfaced as {@link OwnerNotFoundError}
   * so the caller can render the honest "not a proponent" empty state. READ-ONLY: this
   * client exposes no admin accept/decline/promote-on-behalf twin.
   */
  getAdminProponentDashboard: (ownerUid: string): Promise<AdminProponentDashboard> =>
    request<AdminProponentDashboard>(
      `/api/owner/admin/proponents/${encodeSegment(ownerUid)}/dashboard`,
      { method: 'GET' },
    ),

  /**
   * PUT /api/owner/admin/raises/:slug/fields/:field — ADMIN moderation edit of a
   * raise field (god-mode). Server authorizes via `checkAdmin` (no ownership
   * check). Public offering-classification transitions deliberately do NOT use
   * this generic path; they use the attestation twin below. Only reachable from
   * the admin console.
   */
  adminEditRaiseField,

  /**
   * DELETE /api/owner/admin/raises/:slug/fields/:field — ADMIN revert of a raise's
   * overlay field, restoring the sourced/normalized value. Admin-authorized twin of
   * {@link revertOwnerField}.
   */
  adminRevertRaiseField: (slug: string, field: string): Promise<ResolvedOwnerField> =>
    request<ResolvedOwnerField>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/fields/${encodeSegment(field)}`,
      { method: 'DELETE', badRequestKind: 'edit' },
    ),

  /** Admin-authorized attestation twin for a public override. */
  adminAttestOfferingClassification: (
    slug: string,
    input: OfferingClassificationAttestationInput,
  ): Promise<OfferingClassificationMutationResult> =>
    request<OfferingClassificationMutationResult>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/offering-classification/attest`,
      { method: 'POST', body: input, badRequestKind: 'edit' },
    ),

  /** Admin-authorized withdrawal twin; atomically re-restricts the listing. */
  adminWithdrawOfferingClassificationAttestation: (
    slug: string,
  ): Promise<OfferingClassificationMutationResult> =>
    request<OfferingClassificationMutationResult>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/offering-classification/attest`,
      { method: 'DELETE', badRequestKind: 'edit' },
    ),

  /** Admin-only moderation detail, including counsel reference. */
  getOfferingClassificationDetail: (
    slug: string,
  ): Promise<OfferingClassificationDetail> =>
    request<OfferingClassificationDetail>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/offering-classification`,
      { method: 'GET' },
    ),

  /**
   * Admin-only bounded compliance worklist. Pagination is validated client-side
   * for immediate feedback and again with Zod at the server boundary.
   */
  listPublicExposedClassifications: async (
    limit = 50,
    offset = 0,
  ): Promise<PublicExposedClassificationPage> => {
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100) {
      throw new RangeError('Classification worklist limit must be an integer from 1 to 100.');
    }
    if (!Number.isSafeInteger(offset) || offset < 0) {
      throw new RangeError('Classification worklist offset must be a non-negative integer.');
    }
    const search = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const listings = await request<PublicExposedClassificationItem[]>(
      `/api/owner/admin/offering-classification/public-exposed?${search.toString()}`,
      { method: 'GET' },
    );
    return { listings, limit, offset, hasMore: listings.length === limit };
  },

  /**
   * POST /api/owner/connect-requests/:id/accept|decline — the authed, verified owner
   * accepts or declines a Connect request from their dashboard. Returns ONLY the
   * non-PII decision outcome (`id`/`status`/`ownerActionAt`) — never the investor
   * snapshot or any contact PII. 403 → {@link OwnerForbiddenError} (not the owner);
   * 404 → no such request; 409 → already decided the other way.
   */
  respondToConnect: (
    requestId: number,
    action: OwnerConnectAction,
  ): Promise<ConnectDecisionOutcome> =>
    request<ConnectDecisionOutcome>(
      `/api/owner/connect-requests/${encodeSegment(String(requestId))}/${action}`,
      { method: 'POST' },
    ),

  // ── Capital-stack bridge (B2) ────────────────────────────────────────────────

  /**
   * GET /api/owner/raises/:slug/capital-stack — the owner's LATEST VISIBLE
   * capital-stack run (an admin-hidden run is filtered server-side). Resolves to
   * `null` when no visible run exists yet (the panel then shows the "generate" CTA).
   * The verified-owner gate is server-side (403 non-owner / 404 unresolved slug).
   */
  getCapitalStack: (slug: string): Promise<CapitalStackRunDto | null> =>
    capitalStackRequest<CapitalStackRunDto | null>(
      `/api/owner/raises/${encodeSegment(slug)}/capital-stack`,
      { method: 'GET' },
    ),

  /**
   * POST /api/owner/raises/:slug/capital-stack — GENERATE a fresh run for the owner's
   * project (201). Rate-limited 5/hr/uid → {@link CapitalStackRateLimitError} on 429.
   * A listing that cannot be matched → {@link CapitalStackUnsupportedError} (422, with
   * the stable `reason`). 403 non-owner / 404 unresolved slug via the shared mapper.
   */
  generateCapitalStack: (slug: string): Promise<CapitalStackRunDto> =>
    capitalStackRequest<CapitalStackRunDto>(
      `/api/owner/raises/${encodeSegment(slug)}/capital-stack`,
      { method: 'POST' },
    ),

  /**
   * PATCH /api/owner/raises/:slug/capital-stack/:runId/publication — the verified
   * owner publishes/unpublishes a curated run to entitled investors. This is an M1
   * owner tool and remains server-gated; the result echoes both visibility flags.
   */
  setCapitalStackPublication: (
    slug: string,
    runId: number,
    visibleToInvestors: boolean,
  ): Promise<CapitalStackVisibilityDto> =>
    capitalStackRequest<CapitalStackVisibilityDto>(
      `/api/owner/raises/${encodeSegment(slug)}/capital-stack/${encodeSegment(String(runId))}/publication`,
      { method: 'PATCH', body: { visibleToInvestors } },
    ),

  /**
   * GET /api/owner/admin/raises/:slug/capital-stack — ADMIN read of the latest run
   * INCLUDING an owner-hidden one (curation surface). `null` when no run exists.
   * Authorized by `checkAdmin` (no ownership check).
   */
  adminGetCapitalStack: (slug: string): Promise<CapitalStackRunDto | null> =>
    capitalStackRequest<CapitalStackRunDto | null>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/capital-stack`,
      { method: 'GET' },
    ),

  /**
   * POST /api/owner/admin/raises/:slug/capital-stack — ADMIN generates a run for any
   * raise (201). Same 422/429 error algebra as the owner path; admin-authorized.
   */
  adminGenerateCapitalStack: (slug: string): Promise<CapitalStackRunDto> =>
    capitalStackRequest<CapitalStackRunDto>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/capital-stack`,
      { method: 'POST' },
    ),

  /**
   * PATCH /api/owner/admin/raises/:slug/capital-stack/:runId — ADMIN curation: toggle
   * whether a run is visible to the owner. Returns the `{ runId, visibleToOwner }`
   * echo. 404 when no run with that id exists for the slug's project.
   */
  adminSetCapitalStackVisibility: (
    slug: string,
    runId: number,
    visibility: { visibleToOwner?: boolean; visibleToInvestors?: boolean },
  ): Promise<CapitalStackVisibilityDto> =>
    capitalStackRequest<CapitalStackVisibilityDto>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/capital-stack/${encodeSegment(String(runId))}`,
      { method: 'PATCH', body: visibility },
    ),

  /**
   * POST /api/owner/admin/raises/:slug/capital-stack/:runId/recompute-coverage —
   * ADMIN recompute of a run's coverage block (the §2.4 predicate/version-bump safety
   * valve). Mutates ONLY the run's `coverage` key server-side (never stack/tiers);
   * returns the fresh {@link CoverageBlock}. 404 when the runId does not belong to
   * the slug's project. Admin-authorized (checkAdmin), audited server-side.
   */
  adminRecomputeCapitalStackCoverage: (
    slug: string,
    runId: number,
  ): Promise<CoverageBlock> =>
    capitalStackRequest<CoverageBlock>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/capital-stack/${encodeSegment(String(runId))}/recompute-coverage`,
      { method: 'POST' },
    ),

  // ── W3 per-candidate evidence dossier ────────────────────────────────────────
  // Two persona variants mirroring the capital-stack read split: the owner path
  // (verified-ownership gated) and the admin path (checkAdmin). The server IDOR-fences
  // the entity to the raise's OWN latest run — a valid entity NOT in the run 404s, which
  // the shared `request` helper surfaces as a typed OwnerNotFoundError the drawer renders
  // as its honest error state. No entitled/anonymous variant exists (confidential).

  /** GET the owner's dossier for one candidate in their raise's latest visible run. */
  getCandidateDossier: (slug: string, entityId: number): Promise<CandidateDossierDto> =>
    request<CandidateDossierDto>(
      `/api/owner/raises/${encodeSegment(slug)}/capital-stack/candidates/${encodeSegment(String(entityId))}`,
      { method: 'GET' },
    ),

  /** GET the admin dossier (reads INCLUDING an owner-hidden run) for one candidate. */
  adminGetCandidateDossier: (slug: string, entityId: number): Promise<CandidateDossierDto> =>
    request<CandidateDossierDto>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/capital-stack/candidates/${encodeSegment(String(entityId))}`,
      { method: 'GET' },
    ),

  /** GET a paginated page of the candidate's portfolio deals (owner path). */
  getCandidateDeals: (
    slug: string,
    entityId: number,
    params: { after?: number; limit?: number } = {},
  ): Promise<CandidateDossierPage<CandidateDossierDeal>> =>
    request<CandidateDossierPage<CandidateDossierDeal>>(
      `/api/owner/raises/${encodeSegment(slug)}/capital-stack/candidates/${encodeSegment(String(entityId))}/deals${candidateListQuery(params)}`,
      { method: 'GET' },
    ),

  /** GET a paginated page of the candidate's portfolio deals (admin path). */
  adminGetCandidateDeals: (
    slug: string,
    entityId: number,
    params: { after?: number; limit?: number } = {},
  ): Promise<CandidateDossierPage<CandidateDossierDeal>> =>
    request<CandidateDossierPage<CandidateDossierDeal>>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/capital-stack/candidates/${encodeSegment(String(entityId))}/deals${candidateListQuery(params)}`,
      { method: 'GET' },
    ),

  /** GET a paginated page of the candidate's claim evidence (owner path). */
  getCandidateClaims: (
    slug: string,
    entityId: number,
    params: { after?: number; limit?: number } = {},
  ): Promise<CandidateDossierPage<CandidateDossierClaim>> =>
    request<CandidateDossierPage<CandidateDossierClaim>>(
      `/api/owner/raises/${encodeSegment(slug)}/capital-stack/candidates/${encodeSegment(String(entityId))}/claims${candidateListQuery(params)}`,
      { method: 'GET' },
    ),

  /** GET a paginated page of the candidate's claim evidence (admin path). */
  adminGetCandidateClaims: (
    slug: string,
    entityId: number,
    params: { after?: number; limit?: number } = {},
  ): Promise<CandidateDossierPage<CandidateDossierClaim>> =>
    request<CandidateDossierPage<CandidateDossierClaim>>(
      `/api/owner/admin/raises/${encodeSegment(slug)}/capital-stack/candidates/${encodeSegment(String(entityId))}/claims${candidateListQuery(params)}`,
      { method: 'GET' },
    ),

  // ── M2 owner billing (M1 endpoints; frozen) ─────────────────────────────────

  /**
   * GET /api/owner/billing/state — the authed owner's billing state. A verified
   * claimant receives (or lazily starts) their trial state; any other authenticated
   * user gets the explicit non-entitled `{ status: 'none', … }` fallback. A 501 =
   * Stripe deliberately not configured (do NOT retry); a 503 = transient. The FE
   * reads `isEntitled` ONLY — it never re-derives grace from `currentPeriodEnd`.
   */
  getBillingState: (): Promise<OwnerBillingStateResult> =>
    billingRequest<OwnerBillingStateResult>('/api/owner/billing/state', {
      method: 'GET',
    }),

  /**
   * POST /api/owner/billing/checkout-session — create a Stripe-hosted Checkout for
   * the owner (201 `{ id, url }`). The FE sends explicit return URLs so Checkout
   * lands back on the billing page (`?billing=success|canceled`). 501 → not
   * configured; 503 → transient; 409 `checkout_conflict` → already subscribed (route
   * to portal); 409 `trial_alignment_unavailable` → final-48h state.
   */
  createCheckoutSession: (params: {
    successUrl: string;
    cancelUrl: string;
  }): Promise<OwnerBillingSession> =>
    billingRequest<OwnerBillingSession>('/api/owner/billing/checkout-session', {
      method: 'POST',
      body: { successUrl: params.successUrl, cancelUrl: params.cancelUrl },
    }),

  /**
   * POST /api/owner/billing/portal — create a Stripe-hosted Billing-Portal session
   * (201 `{ id, url }`) for managing / cancelling the subscription. The FE sends an
   * explicit `returnUrl` so the portal returns to the billing page
   * (`?billing=portal_return`). Same 501/503 fail-closed algebra; no 409 path.
   */
  createPortalSession: (params: { returnUrl: string }): Promise<OwnerBillingSession> =>
    billingRequest<OwnerBillingSession>('/api/owner/billing/portal', {
      method: 'POST',
      body: { returnUrl: params.returnUrl },
    }),

  // ── Payments v1: project promotions (boost / priority placement) ────────────

  /**
   * GET /api/owner/projects/:projectId/promotions — the verified owner's promotion
   * state for one project: the one-time boost window, the recurring priority
   * subscription, and whether the owner is `eligible` to purchase (owner tools
   * entitled). Routed through {@link billingRequest} (unwraps the `{ success, data }`
   * envelope; typed {@link OwnerBillingApiError} on 402/501/503/403). Server scopes by
   * the authed owner uid; the projectId is never trusted as authorization.
   */
  getProjectPromotions: (projectId: number): Promise<ProjectPromotionState> =>
    billingRequest<ProjectPromotionState>(
      `/api/owner/projects/${encodeSegment(String(projectId))}/promotions`,
      { method: 'GET' },
    ),

  /**
   * POST /api/owner/projects/:projectId/promotions/boost/checkout — create a
   * Stripe-hosted Checkout for the one-time boost ($100 / 30 days). Returns the redirect
   * `url` only; the FE `window.location.assign`es to it (Stripe hosts the payment).
   * 402 `owner_subscription_required` → subscribe paywall; 501 → not configured; 403 →
   * non-owner (all via the shared billing error mapper).
   */
  createPromotionBoostCheckout: (projectId: number): Promise<PromotionCheckoutSession> =>
    billingRequest<PromotionCheckoutSession>(
      `/api/owner/projects/${encodeSegment(String(projectId))}/promotions/boost/checkout`,
      { method: 'POST' },
    ),

  /**
   * POST /api/owner/projects/:projectId/promotions/priority/checkout — create a
   * Stripe-hosted Checkout for the recurring priority placement ($50/mo until
   * cancelled). Returns the redirect `url` only. Same 402/501/403 error algebra.
   */
  createPromotionPriorityCheckout: (projectId: number): Promise<PromotionCheckoutSession> =>
    billingRequest<PromotionCheckoutSession>(
      `/api/owner/projects/${encodeSegment(String(projectId))}/promotions/priority/checkout`,
      { method: 'POST' },
    ),

  /**
   * GET /api/owner/raises/:slug/analytics/views — the owner's listing-view summary
   * (Fork A): 7d/30d view counts, 7d viewer-days, and a dense 14-day daily series.
   * Deliberately NOT billing-gated (the views metric is the renewal signal). The
   * verified-owner gate is server-side (403 non-owner / 404 unresolved slug).
   */
  getViewsAnalytics: (slug: string): Promise<OwnerViewsAnalytics> =>
    request<OwnerViewsAnalytics>(
      `/api/owner/raises/${encodeSegment(slug)}/analytics/views`,
      { method: 'GET' },
    ),

};

export default ownerApi;
